// Dialog for applying changes to multiple tasks at once.
import { useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { useTasksContext } from '@/features/tasks/context/TasksContext.def';
import { useTaskMutationsContext } from '@/features/tasks/context/useTaskMutationsContext';
import {
  extractProjectContextFromTasks,
  buildFieldUpdates,
  renderBulkEditFields,
  applyBulkTagTerms,
  isDurationFieldInvalid,
} from './TaskBulkEditModal.helpers';
import { ensureTermsExist } from '@/services/tagService';
import { useProjectLimitGuard } from '@/features/wall';
import { useT } from '@/i18n/useT';

const UNTOUCHED = Symbol('untouched');

const createEmptyFields = () => ({
  date: UNTOUCHED,
  duration: UNTOUCHED,
  status: UNTOUCHED,
  project: UNTOUCHED,
  context: UNTOUCHED,
  person: UNTOUCHED,
});

const TaskBulkEditModal = ({ open, onClose, selectedTaskIds }) => {
  const t = useT();
  const [fields, setFields] = useState(createEmptyFields);

  const { guardProjects } = useProjectLimitGuard();
  const { allTasks, user, projectDisplayMap, contextDisplayMap } =
    useTasksContext();
  const { updateMultipleTasks } = useTaskMutationsContext();

  const { allProjects, allContexts } = extractProjectContextFromTasks(allTasks);

  const resetFields = () => setFields(createEmptyFields());

  useEffect(() => {
    if (!open) resetFields();
  }, [open]);

  const handleSave = async () => {
    if (!user || !selectedTaskIds?.length) {
      onClose();
      return;
    }

    if (isDurationFieldInvalid(fields.duration, UNTOUCHED)) {
      return;
    }

    const updates = {};
    buildFieldUpdates(fields, updates, UNTOUCHED);

    if (Object.keys(updates).length === 0) {
      onClose();
      return;
    }

    try {
      if (updates.projects?.length && !guardProjects(updates.projects)) {
        return;
      }
      await applyBulkTagTerms(updates, user, ensureTermsExist);
      await updateMultipleTasks(selectedTaskIds, updates, user.uid);
    } catch (error) {
      console.error('[TaskBulkEditModal] Failed to bulk update tasks:', error);
    }

    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {t('taskbulkeditmodal.title', 'Edit {count} tasks', {
          count: selectedTaskIds?.length || 0,
        })}
      </DialogTitle>
      <DialogContent>
        {renderBulkEditFields(
          fields,
          setFields,
          allProjects,
          allContexts,
          projectDisplayMap,
          contextDisplayMap,
          UNTOUCHED,
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          {t('taskbulkeditmodal.cancel', 'Annuleren')}
        </Button>
        <Button onClick={handleSave} variant="contained" color="secondary">
          {t('taskbulkeditmodal.applyChanges', 'Wijzigingen opslaan')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TaskBulkEditModal;
